import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import Loader from '@byma/shared/components/Loader'
import Router, { useRouter } from 'next/router'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useFilters from '@byma/shared/hooks/useFilters'
import DateFilter from '@byma/shared/components/DateFilter'
import EventFilter from '@byma/shared/components/EventFilter'
import useSWRInfinite from 'swr/infinite'

function getCloudinaryURL(url,height) {
    if (typeof url === 'undefined' || !url) return ''
    return `https://res.cloudinary.com/htkavmx5a/image/upload/c_scale,f_auto,h_${height},q_auto/${url.split('/').pop()}`
}

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function LeadsTable() {
    const session = useAuth()
    const { token, user: currentUser } = session
    let { isReady, query, ...router } = useRouter()

    const { originalQueryString, parsedFilters, rawFilters } = useFilters(query, () => true)

    function getKey(pageIndex, previousPageData) {
        if (!token) return null
        if (previousPageData?.result?.length === 0) return null

        if (pageIndex === 0) {
            return [`/api/preleads/getAll?${toQueryString({...parsedFilters, perPage: 50})}`, null, token]
        }

        const lastResult = previousPageData?.result[previousPageData.result.length - 1]
        if (!lastResult) return null

        return [`/api/preleads/getAll?${toQueryString({...parsedFilters, toDate: lastResult.created_at, perPage: 50})}`, null, token]
    }

    const { data: leads, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher)

    const tableData = useMemo(() => {
        if (!leads) {
            return []
        }

        return leads.reduce((acc, cur) => {
            return [...acc, ...(cur?.result || [])]
        }, [])
    }, [leads])

    function handleCSVExport() {
        return fetch(`/api/preleads/export?${toQueryString({ ...parsedFilters })}`, {
            headers: {
                'Content-Type': 'application/json',
                Auth: token,
            }
        })
            .then(req => req.blob())
            .then(res => {
                const blob = new Blob([res], { type: 'application/csv' })
                const file = window.URL.createObjectURL(blob)
                let link = document.createElement('a')
                link.href = file
                link.download = `byma-leads-export-${(new Date()).valueOf()}.csv`
                link.click()
                setTimeout(() => { window.URL.revokeObjectURL(file); }, 250)

            })
    }

    function rowRenderer(index, data) {
        return (
            <div
                key={index}
                className="px-3"
            >
                <LeadItem {...{ lead: tableData[index] || {} }} />
            </div>
        )
    }

    return (
        <>
            <div className="flex space-x-3 p-3 shadow w-full overflow-y-auto">
                <DateFilter activeKey={rawFilters.dateKey} />
                <EventFilter token={token} filteredEvents={rawFilters.parties} />
                <button onClick={handleCSVExport}>
                    <div className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <p>
                            Exportar para CSV
                        </p>
                    </div>
                </button>
            </div>
            <Virtuoso
                data={tableData || []}
                endReached={() => { console.log('setting size as ', size + 1); setSize(size+1)}}
                overscan={200}
                itemContent={rowRenderer}
                components={{
                    Header: () => {
                        return (
                            <div
                                className="h-2"
                            />
                        )
                    },
                    Footer: () => {
                        return (
                            <div
                                className="p-3 flex items-center justify-center"
                            >
                                { isValidating ? (
                                    <Loader /> 
                                ): (
                                    <p className="text-sm text-gray-700">
                                        { tableData.length === 0 ? 'Nenhum resultado encontrado' : 'Fim dos resultados' }
                                    </p>
                                )}
                            </div>
                        )
                    }
                }}
            />
        </>
    )
}

function LeadItem ({ lead, active = false }) {
    return (
        <div className={`flex justify-between items-center box box-hover ${active && 'box-active'}`}>
            <div className="flex items-center min-w-0">

                <div className="p-3 flex flex-col items-center justify-center space-y-2">
                    {lead?.partyId?.pictures?.photos[0] ? (
                        <div className="rounded-full w-12 h-12 min-w-0 overflow-hidden">
                            <img src={getCloudinaryURL(lead.partyId.pictures.photos[0], 60)} alt={lead.partyId.name} className="rounded-full w-12 h-12 object-cover" width={60} height={60} />
                        </div>
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-400 overflow-hidden min-w-0" />
                    )}
                </div>

                <div className="min-w-0">
                    <p className="text-gray-700">{dayjs(lead.created_at).format('DD/MM/YYYY HH:mm:ss')}</p>
                    <p className="text-md truncate">
                        {lead.partyId?.name}
                    </p>
                    <div className="flex space-x-1 items-center">
                        {lead?.user?.profile?.picture ? (
                            <div className="rounded-full w-6 h-6 min-w-0">
                                <img src={lead.user.profile.picture} className="rounded-full w-6 h-6 object-cover" alt="" width={20} height={20} />
                            </div>
                        ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-400 overflow-hidden min-w-0" />
                        )}
                        <p className="text-md text-gray-700 truncate">
                            {lead.user?.profile?.name || 'Usuário não associado'}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
